import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { Button, ButtonSizes, ToastTypes } from 'atoms';
import {
  getCareerJourneyErrorText,
  useApplyPrompt,
  useCareerApplicationActions,
  useToaster,
} from 'core';

/**
 * «Вы отправили заявку?» после возврата с сайта работодателя (ТЗ 0069).
 *
 * 🔴 НЕ модальное окно, а `role="status"`. Модалка, крадущая фокус ровно в тот
 * момент, когда человек переключился обратно на вкладку, — враждебное
 * поведение: он вернулся не к нам, а к тому, что делал.
 *
 * 🔴 Отклик создаётся ТОЛЬКО по явному «да». Надёжного сигнала «человек
 * откликнулся» не существует, и автоматически проставленный отклик отравил бы
 * и журнал, и оценку готовности к работе — причём объяснить его пользователь
 * не смог бы.
 *
 * «Нет» — тоже ответ: намерение стирается, и вопрос не возвращается.
 */
const TOAST_KEY = 'career-apply-prompt';

export const CareerApplyPrompt = memo(() => {
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const { intent, clear } = useApplyPrompt();
  const { trackApplication, loading } = useCareerApplicationActions();
  const [busy, setBusy] = useState(false);

  if (!intent) {
    return null;
  }

  const confirm = async () => {
    setBusy(true);
    try {
      const result = await trackApplication({
        vacancyId: intent.vacancyId || null,
        companyName: intent.companyName,
        position: intent.position,
        applyUrl: intent.applyUrl,
        status: 'applied',
      });
      // 🔴 Отказ сервера обязан быть слышен. Раньше результат не читался
      // вовсе: не-участник жал «Да», сервер отвечал `not_a_member`, отклик не
      // появлялся, намерение стиралось — и человеку не говорили ничего.
      if (result.errorCode) {
        addToast(TOAST_KEY, {
          type: ToastTypes.danger,
          // По КОДУ, а не по тексту сервера: сообщение сервера русское.
          text: getCareerJourneyErrorText(t, result.errorCode),
        });
      }
    } finally {
      // 🔴 `finally`, а не хвост try: при сетевой ошибке `await` бросал, и
      // кнопка оставалась выключенной навсегда, а намерение — в хранилище.
      setBusy(false);
      clear();
    }
  };

  return (
    <Bar role="status">
      <Text>
        {t('careerCenter.applications.prompt.question', {
          company: intent.companyName,
        })}
      </Text>
      <Actions>
        <Button size={ButtonSizes.sm} onClick={confirm} disabled={busy || loading}>
          {t('careerCenter.applications.prompt.yes')}
        </Button>
        <Button variant="text" size={ButtonSizes.sm} onClick={clear}>
          {t('careerCenter.applications.prompt.no')}
        </Button>
      </Actions>
    </Bar>
  );
});

const Bar = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid ${Colors.system_gray};
  border-left: 4px solid ${Colors.main};
  border-radius: 10px;
  background: ${Colors.white};

  ${mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Text = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;
